/**
 * ColorUtilitiesSection.tsx
 * Color Utilities section showing background, text, and border color utilities
 */

import React from 'react';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';

// Color scales available in the theme
const colorScales = [
  { name: 'primary', desc: 'Primary brand color (Blue)' },
  { name: 'default', desc: 'Default/Neutral (Gray)' },
  { name: 'secondary', desc: 'Secondary brand (Teal)' },
  { name: 'success', desc: 'Success states (Green)' },
  { name: 'warning', desc: 'Warning states (Orange)' },
  { name: 'danger', desc: 'Danger/Error states (Red)' },
  { name: 'blue', desc: 'Blue utility scale' },
  { name: 'gray', desc: 'Gray utility scale' },
  { name: 'green', desc: 'Green utility scale' },
  { name: 'red', desc: 'Red utility scale' },
] as const;

export const ColorUtilitiesSection: React.FC = () => {
  return (
    <div className="space-y-6">
      <Card shadow="lg" isHoverable>
        <CardHeader>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-text-primary">Color Utilities</h3>
            <Divider />
            <p className="text-text-secondary">Background, text, and border color utilities - updates with theme toggle</p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-8">
            {/* Background Colors */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Colors on Backgrounds</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {colorScales.map((scale) => (
                  <div key={`bg-${scale.name}`} className="space-y-2">
                    <h5 className="text-sm font-medium text-text-secondary capitalize">{scale.name}</h5>
                    <div className="space-y-1">
                      <div 
                        className="p-3 rounded-lg text-sm font-medium"
                        style={{ 
                          backgroundColor: `var(--color-${scale.name}-100)`,
                          color: `var(--color-${scale.name}-900)`
                        }}
                      >
                        bg-{scale.name}-100
                      </div>
                      <div 
                        className="p-3 rounded-lg text-white text-sm font-medium"
                        style={{ backgroundColor: `var(--color-${scale.name}-500)` }}
                      >
                        bg-{scale.name}-500
                      </div>
                      <div 
                        className="p-3 rounded-lg text-white text-sm font-medium"
                        style={{ backgroundColor: `var(--color-${scale.name}-900)` }}
                      >
                        bg-{scale.name}-900
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Text Colors */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Colors on Text</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {colorScales.map((scale) => (
                  <div key={`text-${scale.name}`} className="space-y-2">
                    <h5 className="text-sm font-medium text-text-secondary capitalize">{scale.name}</h5>
                    <div className="space-y-1">
                      <p 
                        className="text-sm"
                        style={{ color: `var(--color-${scale.name}-400)` }}
                      >
                        text-{scale.name}-400: Sample text content
                      </p>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: `var(--color-${scale.name}-500)` }}
                      >
                        text-{scale.name}-500: Sample text content
                      </p>
                      <p 
                        className="text-sm font-semibold"
                        style={{ color: `var(--color-${scale.name}-600)` }}
                      >
                        text-{scale.name}-600: Sample text content
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Border Colors */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Colors on Borders</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {colorScales.map((scale) => (
                  <div key={`border-${scale.name}`} className="space-y-2">
                    <h5 className="text-sm font-medium text-text-secondary capitalize">{scale.name}</h5>
                    <div className="space-y-2">
                      <div 
                        className="p-3 rounded-lg border-2 bg-background text-sm"
                        style={{ borderColor: `var(--color-${scale.name}-200)` }}
                      >
                        border-{scale.name}-200
                      </div>
                      <div 
                        className="p-3 rounded-lg border-2 bg-background text-sm"
                        style={{ borderColor: `var(--color-${scale.name}-500)` }}
                      >
                        border-{scale.name}-500
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Semantic Colors */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Semantic Color Usage</h4>
              <div className="grid gap-4">
                <Card className="p-4 bg-background">
                  <h5 className="font-medium text-text-primary mb-2">Text Hierarchy</h5>
                  <div className="space-y-2">
                    <p className="text-text-primary text-base">Primary text: Main content and headings</p>
                    <p className="text-text-secondary text-sm">Secondary text: Supporting information</p>
                    <p className="text-text-tertiary text-xs">Tertiary text: Subtle details and captions</p>
                  </div>
                </Card>

                <Card className="p-4 bg-surface">
                  <h5 className="font-medium text-text-primary mb-2">Surface Backgrounds</h5>
                  <p className="text-text-secondary text-sm">Surface background with proper text contrast</p>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-4 bg-primary">
                    <h5 className="font-medium text-text-inverse mb-2">Primary Background</h5>
                    <p className="text-text-inverse/80 text-sm">Inverse text on primary background</p>
                  </Card>
                  
                  <Card className="p-4 bg-secondary">
                    <h5 className="font-medium text-text-inverse mb-2">Secondary Background</h5>
                    <p className="text-text-inverse/80 text-sm">Inverse text on secondary background</p>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};